/**
 * プロジェクト関連のサービス
 * プロジェクトの作成、初期化、管理を行う
 */

import packageInfo from '../../../package.json';
import {
  MIDASProject,
  DataSet,
  Workspace,
  DatasetId,
  createWorkspaceId,
  createProjectId,
  createPaneId,
  createTabId,
  ProjectState
} from '../../types';
import type { TabContainer } from '../../types/pane.types';
import { initializeProjectState } from '../utils/projectUtils';
import { createInitialLayout, getFirstContainerId } from '../utils/layoutUtils';
import logger from '../utils/logger';

/**
 * データセットから新しいプロジェクトを作成
 */
export function createProjectFromDataset(dataset: DataSet): MIDASProject {
  const projectId = createProjectId(`project-${crypto.randomUUID()}`);
  
  logger.info('[projectService] Creating project from dataset', {
    projectId,
    datasetId: dataset.id,
    datasetName: dataset.name
  });
  
  // データセット辞書を作成（DatasetIdをキーとする）
  const datasets: Record<DatasetId, DataSet> = {};
  datasets[dataset.id] = dataset;
  
  // 基本的なプロジェクト構造を作成（projectStateは後で初期化）
  const project: MIDASProject = {
    id: projectId,
    projectName: dataset.name,
    version: packageInfo.version,  // package.jsonから現在のバージョンを使用
    originalFilePath: '',  // Web版ではファイルパスは保持しない
    datasets,
    models: {},
    reports: [],
    projectState: {} as ProjectState, // 一時的なプレースホルダー、後で初期化
    workspaces: [],
    metadata: {
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString(),
      description: `Project created from ${dataset.name}`
    }
  };

  // プロジェクト状態を初期化
  project.projectState = initializeProjectState(project);
  
  // デフォルトワークスペースを作成（初期レイアウト付き）
  const defaultWorkspaceId = createWorkspaceId(`workspace-${crypto.randomUUID()}`);
  const initialLayout = createInitialLayout(project);
  const firstContainerId = getFirstContainerId(initialLayout);
  const defaultWorkspace: Workspace = {
    id: defaultWorkspaceId,
    name: 'Main',
    layoutState: {
      rootPane: initialLayout,
      activeContainerId: firstContainerId || undefined
    }
  };

  project.workspaces = [defaultWorkspace];
  project.activeWorkspaceId = defaultWorkspaceId;
  
  // 空のレポート配列を初期化
  project.reports = [];
  
  logger.info('[projectService] Project created successfully', {
    projectId: project.id,
    datasetCount: Object.keys(project.datasets).length,
    workspaceCount: project.workspaces.length
  });
  
  return project;
}

/**
 * MDSファイルから読み込んだプロジェクトを初期化
 */
export function initializeLoadedProject(projectData: Partial<MIDASProject> & { id: string; projectName: string }): MIDASProject {
  logger.info('[projectService] Initializing loaded project', {
    projectId: projectData.id,
    hasDatasets: !!projectData.datasets,
    hasWorkspaces: !!projectData.workspaces
  });
  
  // 必須フィールドを確認
  if (!projectData.id || !projectData.projectName) {
    throw new Error('Invalid project file: missing required fields');
  }

  // プロジェクト構造を構築
  const project: MIDASProject = {
    ...projectData,
    id: createProjectId(projectData.id),
    projectName: projectData.projectName,
    originalFilePath: projectData.originalFilePath || '',
    datasets: projectData.datasets || {},
    models: projectData.models || {},
    workspaces: projectData.workspaces || [],
    reports: projectData.reports || [],
    projectState: projectData.projectState || ({} as ProjectState),  // 後で初期化
    metadata: projectData.metadata || {
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    },
    version: projectData.version || '1.0.0'
  };

  // プロジェクト状態がない場合は初期化
  if (!projectData.projectState) {
    project.projectState = initializeProjectState(project);
  }
  
  // ワークスペースがない場合はデフォルトを作成
  if (project.workspaces.length === 0) {
    const defaultWorkspaceId = createWorkspaceId(`workspace-${crypto.randomUUID()}`);
    // 空のコンテナを作成
    const emptyContainer: TabContainer = {
      type: 'container',
      id: createPaneId('root-container'),
      activeTabId: createTabId(''),
      tabs: []
    };
    const defaultWorkspace: Workspace = {
      id: defaultWorkspaceId,
      name: 'Main',
      layoutState: {
        rootPane: emptyContainer,
        activeContainerId: emptyContainer.id
      }
    };
    project.workspaces = [defaultWorkspace];
    project.activeWorkspaceId = defaultWorkspaceId;
  }
  
  return project;
}